import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ClipboardCheck, Plus, Trash2 } from 'lucide-react';
import { ToolLayout } from '../../components/ToolLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { printPreview } from '../../lib/print';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';

const blank = () => ({ competencyTitle: 'Annual Skills Competency', facilityName: '', department: '', validityPeriod: 'Annual', passingCriteria: 'Must demonstrate all required skills', skills: [{ skill: 'Hand hygiene technique', required: true }, { skill: 'Medication administration', required: true }, { skill: 'Documentation accuracy', required: false }] });

export default function Competency() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const { toast } = useToast();
  const previewRef = useRef(null);
  const [data, setData] = useState(blank());
  const [docId, setDocId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const id = params.get('id');
    if (id) api.get(`/onepagers/${id}`).then((r) => { setData(r.data.data || blank()); setDocId(id); }).catch(() => {});
  }, [params]);

  const up = (patch) => setData((d) => ({ ...d, ...patch }));
  const setSkill = (i, patch) => { const s = [...data.skills]; s[i] = { ...s[i], ...patch }; up({ skills: s }); };

  const save = async () => {
    if (!user) { toast({ title: 'Sign in to save', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      const payload = { title: data.competencyTitle, docType: 'competency-checklist', layout: 'competency-validation', data, colorScheme: { primary: '#0D9488', background: '#F0FDFA' } };
      const res = docId ? await api.put(`/onepagers/${docId}`, payload) : await api.post('/onepagers', payload);
      setDocId(res.data.id); toast({ title: 'Saved to library!' });
    } catch { toast({ title: 'Save failed', variant: 'destructive' }); } finally { setSaving(false); }
  };

  return (
    <ToolLayout title="Skills Competency" subtitle="Annual validation checklist" icon={ClipboardCheck} onSave={save} saving={saving} onPrint={() => printPreview(previewRef.current)} onNew={() => { setData(blank()); setDocId(null); }}>
      <div className="grid lg:grid-cols-[380px_1fr] gap-6">
        <Card><CardContent className="p-4 space-y-3">
          <div><Label className="text-xs">Competency Title</Label><Input value={data.competencyTitle} onChange={(e) => up({ competencyTitle: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-2"><div><Label className="text-xs">Facility</Label><Input value={data.facilityName} onChange={(e) => up({ facilityName: e.target.value })} /></div><div><Label className="text-xs">Department</Label><Input value={data.department} onChange={(e) => up({ department: e.target.value })} /></div></div>
          <div className="grid grid-cols-2 gap-2"><div><Label className="text-xs">Validity Period</Label><Input value={data.validityPeriod} onChange={(e) => up({ validityPeriod: e.target.value })} /></div><div><Label className="text-xs">Passing Criteria</Label><Input value={data.passingCriteria} onChange={(e) => up({ passingCriteria: e.target.value })} /></div></div>
          <div className="flex items-center justify-between"><Label className="text-xs">Skills</Label><Button size="sm" variant="ghost" onClick={() => up({ skills: [...data.skills, { skill: 'New skill', required: false }] })}><Plus className="w-3 h-3" /></Button></div>
          {data.skills.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input value={s.skill} onChange={(e) => setSkill(i, { skill: e.target.value })} className="text-xs" />
              <label className="flex items-center gap-1 text-[11px] shrink-0"><Checkbox checked={s.required} onCheckedChange={(v) => setSkill(i, { required: !!v })} /> Req</label>
              <Button size="icon" variant="ghost" className="text-red-500 shrink-0" onClick={() => up({ skills: data.skills.filter((_, j) => j !== i) })}><Trash2 className="w-3 h-3" /></Button>
            </div>
          ))}
        </CardContent></Card>
        <div className="border rounded-xl bg-gray-100 p-4">
          <div ref={previewRef} className="onepager-preview bg-white p-8 shadow-lg mx-auto" style={{ width: '8.5in', minHeight: '11in', fontFamily: 'Inter' }}>
            <div className="text-center border-b-2 border-teal-600 pb-3 mb-4">
              <h1 className="text-2xl font-bold text-teal-700">{data.competencyTitle}</h1>
              <p className="text-sm text-gray-600">{data.facilityName} {data.department && `• ${data.department}`}</p>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs mb-4 bg-teal-50 rounded-lg p-3">
              <div><b>Employee:</b> _____________________</div><div><b>Date:</b> _______________</div>
              <div><b>Validity:</b> {data.validityPeriod}</div><div><b>Passing:</b> {data.passingCriteria}</div>
            </div>
            <table className="w-full text-sm"><thead><tr className="text-left text-teal-700 border-b"><th className="pb-1">Skill</th><th className="pb-1 w-16">Required</th><th className="pb-1 w-16">Met</th></tr></thead>
              <tbody>{data.skills.map((s, i) => (<tr key={i} className="border-b border-gray-100"><td className="py-2">{s.skill}</td><td>{s.required ? 'Yes' : 'Optional'}</td><td><span className="checklist-checkbox inline-block w-4 h-4 border-2 border-teal-600 rounded" /></td></tr>))}</tbody>
            </table>
            <div className="mt-8 grid grid-cols-2 gap-6 text-sm"><div className="border-t pt-1">Employee Signature / Date</div><div className="border-t pt-1">Evaluator Signature / Date</div></div>
          </div>
        </div>
      </div>
    </ToolLayout>
  );
}
